import axios from 'axios';

export interface ApiError {
  message: string;
  statusCode?: number;
  originalError?: any;
  timestamp: number;
}

export class ServiceError extends Error {
  public readonly statusCode?: number;
  public readonly originalError?: any;
  public readonly timestamp: number;

  constructor(message: string, statusCode?: number, originalError?: any) {
    super(message);
    this.name = 'ServiceError';
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.timestamp = Date.now();
  }
}

/**
 * Handles API errors consistently across all services
 */
export function handleApiError(error: any): ServiceError {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const axiosError = error;
    
    if (axiosError.response) {
      // Server responded with error status
      const { status, data } = axiosError.response;
      
      // Extract error message from response
      let message = `HTTP ${status}: ${axiosError.response.statusText}`;
      
      if (data && typeof data === 'object') {
        if ('error' in data) {
          message = data.error;
        } else if ('message' in data) {
          message = data.message;
        } else if ('detail' in data) {
          message = data.detail;
        } else if ('errors' in data && Array.isArray(data.errors)) {
          message = data.errors.join(', ');
        }
      }
      
      return new ServiceError(message, status, axiosError);
    } else if (axiosError.request) {
      // Network error
      return new ServiceError(
        'Network error: Unable to connect to the server',
        0,
        axiosError
      );
    } else {
      // Request setup error
      return new ServiceError(
        'Request configuration error',
        0,
        axiosError
      );
    }
  }

  // Handle custom ServiceError
  if (error instanceof ServiceError) {
    return error;
  }

  // Handle generic Error
  if (error instanceof Error) {
    return new ServiceError(error.message, undefined, error);
  }

  // Handle unknown error types
  return new ServiceError(
    'An unknown error occurred',
    undefined,
    error
  );
}

/**
 * Wraps service methods with consistent error handling
 */
export function withErrorHandling<T extends any[], R>(
  fn: (...args: T) => Promise<R>
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      throw handleApiError(error);
    }
  };
}

/**
 * Creates a standardized error response for API failures
 */
export function createErrorResponse(error: ServiceError): {
  success: false;
  error: ApiError;
} {
  return {
    success: false,
    error: {
      message: error.message,
      statusCode: error.statusCode,
      originalError: error.originalError,
      timestamp: error.timestamp,
    },
  };
}

/**
 * Creates a standardized success response
 */
export function createSuccessResponse<T>(data: T): {
  success: true;
  data: T;
} {
  return {
    success: true,
    data,
  };
}

/**
 * Type guard to check if a response is a success response
 */
export function isSuccessResponse<T>(
  response: any
): response is { success: true; data: T } {
  return response && typeof response === 'object' && response.success === true;
}

/**
 * Type guard to check if a response is an error response
 */
export function isErrorResponse(
  response: any
): response is { success: false; error: ApiError } {
  return response && typeof response === 'object' && response.success === false;
}

/**
 * Utility to log errors in a consistent format
 */
export function logError(error: ServiceError, context?: string): void {
  const logData = {
    message: error.message,
    statusCode: error.statusCode,
    timestamp: new Date(error.timestamp).toISOString(),
    context,
  };

  if (error.statusCode && error.statusCode >= 500) {
    console.error('Service Error (Server):', logData);
  } else if (error.statusCode && error.statusCode >= 400) {
    console.warn('Service Error (Client):', logData);
  } else {
    console.error('Service Error (Unknown):', logData);
  }
}

/**
 * Retry mechanism for failed API calls
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: ServiceError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = handleApiError(error);

      // Don't retry on client errors (4xx)
      if (lastError.statusCode && lastError.statusCode >= 400 && lastError.statusCode < 500) {
        throw lastError;
      }

      if (attempt === maxRetries) {
        throw lastError;
      }

      // Exponential backoff
      const backoffDelay = delay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }

  throw lastError!;
}